import React, { useState, useEffect } from "react";
import EmailAnalysis from "./EmailAnalysis";
import EmailAnalysisSimple from "./EmailAnalysisSimple";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";

const EmailAnalysisContainer = ({ setHeroContent }) => {
  const [isSimpleView, setIsSimpleView] = useState(true); // Start with simple view by default
  const [targetAnalysisKey, setTargetAnalysisKey] = useState(null);

  // Function to read URL parameters
  const readUrlParameters = () => {
    console.log("EmailAnalysisContainer: Reading URL parameters");
    console.log("EmailAnalysisContainer: Current URL:", window.location.href);

    const urlParams = new URLSearchParams(window.location.search);
    const analysisKey = urlParams.get("analysisKey");
    const autoSelect = urlParams.get("autoSelect");
    const showDetails = urlParams.get("showDetails");

    console.log("EmailAnalysisContainer: Found parameters:", {
      analysisKey,
      autoSelect,
      showDetails,
    });

    if (analysisKey && autoSelect === "true") {
      console.log(
        "EmailAnalysisContainer: Auto-selecting analysis with key:",
        analysisKey,
      );
      setTargetAnalysisKey(analysisKey);

      // Store showDetails flag for child components to use
      if (showDetails === "true") {
        console.log("EmailAnalysisContainer: Setting showDetails flag");
        localStorage.setItem("notificationShowDetails", "true");
      }
    } else {
      console.log("EmailAnalysisContainer: No auto-selection parameters found");
    }
  };

  // Check for URL parameters on mount and when URL changes
  useEffect(() => {
    console.log(
      "EmailAnalysisContainer: Component mounted/updated, checking URL parameters",
    );

    // Initial read
    readUrlParameters();

    // Listen for popstate events (browser back/forward)
    const handlePopState = () => {
      console.log("EmailAnalysisContainer: popstate event detected");
      readUrlParameters();
    };

    // Listen for pushstate/replacestate events (programmatic navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      console.log("EmailAnalysisContainer: pushState detected");
      setTimeout(readUrlParameters, 100); // Increased timeout
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      console.log("EmailAnalysisContainer: replaceState detected");
      setTimeout(readUrlParameters, 100); // Increased timeout
    };

    // Add event listeners
    window.addEventListener("popstate", handlePopState);

    // Custom event listener for notification navigation
    const handleNotificationNavigation = (event) => {
      console.log(
        "EmailAnalysisContainer: Received notification-navigation event:",
        event.detail,
      );
      setTimeout(readUrlParameters, 100); // Increased timeout
    };

    window.addEventListener(
      "notification-navigation",
      handleNotificationNavigation,
    );

    // Also listen for a simple URL change event
    const handleUrlChange = () => {
      console.log("EmailAnalysisContainer: url-changed event detected");
      readUrlParameters();
    };

    window.addEventListener("url-changed", handleUrlChange);

    // Cleanup
    return () => {
      console.log("EmailAnalysisContainer: Cleaning up event listeners");
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(
        "notification-navigation",
        handleNotificationNavigation,
      );
      window.removeEventListener("url-changed", handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // Also add an interval-based check as a fallback
  useEffect(() => {
    const intervalId = setInterval(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const analysisKey = urlParams.get("analysisKey");
      const autoSelect = urlParams.get("autoSelect");

      if (
        analysisKey &&
        autoSelect === "true" &&
        targetAnalysisKey !== analysisKey
      ) {
        console.log(
          "EmailAnalysisContainer: Interval check found new analysis key:",
          analysisKey,
        );
        setTargetAnalysisKey(analysisKey);
      }
    }, 1000); // Check every second

    return () => {
      clearInterval(intervalId);
    };
  }, [targetAnalysisKey]);

  // Debug effect to monitor targetAnalysisKey changes
  useEffect(() => {
    console.log(
      "EmailAnalysisContainer: targetAnalysisKey changed to:",
      targetAnalysisKey,
    );
  }, [targetAnalysisKey]);

  const handleToggleView = () => {
    setIsSimpleView(!isSimpleView);
  };

  const handleTargetSelected = () => {
    setTargetAnalysisKey(null);

    // Clear URL parameters and localStorage flags after successful selection
    if (window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.delete("analysisKey");
      url.searchParams.delete("autoSelect");
      url.searchParams.delete("showDetails");
      window.history.replaceState({}, "", url);
    }
    // Clear localStorage flag
    localStorage.removeItem("notificationShowDetails");
  };

  return (
    <>
      <ReadOnlyBanner
        onReturn={() => setHeroContent && setHeroContent("Member User Info")}
      />
      {isSimpleView ? (
        <EmailAnalysisSimple
          onToggleView={handleToggleView}
          targetAnalysisKey={targetAnalysisKey}
          onTargetSelected={handleTargetSelected}
          setHeroContent={setHeroContent}
        />
      ) : (
        <EmailAnalysis
          onToggleView={handleToggleView}
          targetAnalysisKey={targetAnalysisKey}
          onTargetSelected={handleTargetSelected}
        />
      )}
    </>
  );
};

export default EmailAnalysisContainer;
